import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalId } from '../../types';
import { toObjectiveIdInput } from '../../mappings';
import { GoalAlignedTo } from './GoalAlignedTo';
import { GoalRelatedGoal } from '../../types';
import { useToasts } from '../../../../../../../context/Toast';
import {
  useAddObjectiveAlignmentMutation,
  useRemoveObjectiveAlignmentMutation,
} from '../../../../../__generated__/graphql';

type Props = {
  goalId: GoalId;
  alignedTo: GoalRelatedGoal[];
  onLinkClick?: () => void;
};

export const GoalAlignedToConnected: React.FC<Props> = ({
  goalId,
  alignedTo,
  onLinkClick,
}) => {
  const { t } = useTranslation();
  const { addSuccess } = useToasts();

  const [addObjectiveAlignment, { loading: isAdding }] =
    useAddObjectiveAlignmentMutation();
  const [removeObjectiveAlignment, { loading: isRemoving }] =
    useRemoveObjectiveAlignmentMutation({});

  const isProcessing = isAdding || isRemoving;

  const handleAdd = useCallback(
    async (targetGoalId: GoalRelatedGoal) => {
      await addObjectiveAlignment({
        variables: {
          input: {
            sourceObjectiveId: toObjectiveIdInput(goalId),
            targetObjectiveId: toObjectiveIdInput(targetGoalId),
          },
        },
      });
    },
    [addObjectiveAlignment, goalId],
  );

  const handleRemove = useCallback(
    async (targetGoalId: GoalRelatedGoal) => {
      await removeObjectiveAlignment({
        variables: {
          input: {
            sourceObjectiveId: toObjectiveIdInput(goalId),
            targetObjectiveId: toObjectiveIdInput(targetGoalId),
          },
        },
      });
      addSuccess(t('performanceReview.goals2.goalAlignedTo.removeSuccess'));
    },
    [removeObjectiveAlignment, goalId, addSuccess, t],
  );

  if (goalId.type !== 'employee') {
    return null;
  }

  return (
    <GoalAlignedTo
      sourceGoalId={goalId}
      alignedTo={alignedTo}
      onLinkClick={onLinkClick}
      onAdd={handleAdd}
      onRemove={handleRemove}
      isProcessing={isProcessing}
    />
  );
};
